import React, { useEffect, useMemo, useRef, useState } from "react";

import { useGetFootprintingDairyCowSalesByPrimaryDataId, useGetFootprintingDairyHerdSize, useMutateFootprintingDairyCowSales, useTranslation } from "utils";

import { AlertBanner, Card, Flex, Form, FormInstance, InputNumber, PageSpin } from "components";

interface CowSoldProps {
  year: number;
  farmId: string;
  primaryDataId?: string;
  onComplete?: () => void;
  readOnly?: boolean;
  onNavigateToStep?: (step: number) => void;
}

export const CommercialClassesForm = ({ primaryDataId, readOnly, farmId, year, onNavigateToStep }: CowSoldProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [cowSalesInPoorCondition, setCowSalesInPoorCondition] = useState<number | null>(null);
  const [cowSalesForLightManufacturing, setCowSalesForLightManufacturing] = useState<number | null>(null);
  const [cowSalesForStandardManufacturing, setCowSalesForStandardManufacturing] = useState<number | null>(null);
  const [cowSalesInGoodCondition, setCowSalesInGoodCondition] = useState<number | null>(null);
  const [cowSalesInPremiumCondition, setCowSalesInPremiumCondition] = useState<number | null>(null);

  const [totalHerdSize, setTotalHerdSize] = useState<number>(0);

  const totalCowSales = useMemo(
    () =>
      [
        cowSalesInPoorCondition,
        cowSalesForLightManufacturing,
        cowSalesForStandardManufacturing,
        cowSalesInGoodCondition,
        cowSalesInPremiumCondition,
      ].reduce<number>((sum, val) => sum + (val ?? 0), 0),
    [cowSalesInPoorCondition, cowSalesForLightManufacturing, cowSalesForStandardManufacturing, cowSalesInGoodCondition, cowSalesInPremiumCondition]
  );
  const exceedsHerdSize = totalCowSales > totalHerdSize;

  const { data: cowSalesData, isLoading } = useGetFootprintingDairyCowSalesByPrimaryDataId(primaryDataId ?? "");
  const { data: herdSize } = useGetFootprintingDairyHerdSize(primaryDataId || "", { enabled: !!primaryDataId });

  const { mutate: mutateCowSales } = useMutateFootprintingDairyCowSales(
    farmId,
    year,
    {
      cowSalesInPoorCondition,
      cowSalesForLightManufacturing,
      cowSalesForStandardManufacturing,
      cowSalesInGoodCondition,
      cowSalesInPremiumCondition,
    },
    primaryDataId ?? ""
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCowSales();
      }
    },
    [isLoading, mutateCowSales, readOnly]
  );

  useEffect(() => {
    if (herdSize && herdSize.length > 0) {
      const total = herdSize.reduce((sum, herd) => sum + (herd.cowCount || 0), 0);
      setTotalHerdSize(total);
    }
  }, [herdSize]);

  // Load existing data from sold cows table
  useEffect(() => {
    if (cowSalesData) {
      setCowSalesInPoorCondition(cowSalesData.cowSalesInPoorCondition ?? null);
      setCowSalesForLightManufacturing(cowSalesData.cowSalesForLightManufacturing ?? null);
      setCowSalesForStandardManufacturing(cowSalesData.cowSalesForStandardManufacturing ?? null);
      setCowSalesInGoodCondition(cowSalesData.cowSalesInGoodCondition ?? null);
      setCowSalesInPremiumCondition(cowSalesData.cowSalesInPremiumCondition ?? null);

      formRef.current?.setFieldsValue({
        cowSalesInPoorCondition: cowSalesData.cowSalesInPoorCondition ?? null,
        cowSalesForLightManufacturing: cowSalesData.cowSalesForLightManufacturing ?? null,
        cowSalesForStandardManufacturing: cowSalesData.cowSalesForStandardManufacturing ?? null,
        cowSalesInGoodCondition: cowSalesData.cowSalesInGoodCondition ?? null,
        cowSalesInPremiumCondition: cowSalesData.cowSalesInPremiumCondition ?? null,
      });

      // Validate if any fields have values
      const hasValues = [
        cowSalesData.cowSalesInPoorCondition,
        cowSalesData.cowSalesForLightManufacturing,
        cowSalesData.cowSalesForStandardManufacturing,
        cowSalesData.cowSalesInGoodCondition,
        cowSalesData.cowSalesInPremiumCondition,
      ].some((val) => val !== null && val !== undefined);

      if (hasValues && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [cowSalesData]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-75 padding-right-75 margin-left-100 margin-right-100">
        {t("dairyHerdCommercialClassesForm.title")}
      </h1>
      <Card className="padding-200 margin-top-100">
        <Form ref={formRef}>
          <Flex vertical gap={24}>
            <Form.Item validateStatus={exceedsHerdSize ? "error" : ""}>
              <InputNumber
                disabled={readOnly}
                name="cowSalesInPoorCondition"
                dataTestId="footprinting-herd-size-calves-input"
                label={t("dairyHerdCommercialClassesForm.question.poorConditionInput")}
                suffix={t("dairyHerdCommercialClassesForm.question.commercialClassesSuffix")}
                value={cowSalesInPoorCondition}
                initialValue={cowSalesInPoorCondition}
                onChange={(v) => {
                  setCowSalesInPoorCondition(v);
                }}
                validateFirst
                rules={[{ required: false }, { type: "number" }]}
              />
            </Form.Item>

            <Form.Item validateStatus={exceedsHerdSize ? "error" : ""}>
              <InputNumber
                disabled={readOnly}
                name="cowSalesForLightManufacturing"
                dataTestId="footprinting-herd-size-calves-input"
                label={t("dairyHerdCommercialClassesForm.question.lightManufacturingInput")}
                suffix={t("dairyHerdCommercialClassesForm.question.commercialClassesSuffix")}
                value={cowSalesForLightManufacturing}
                initialValue={cowSalesForLightManufacturing}
                onChange={(v) => {
                  setCowSalesForLightManufacturing(v);
                }}
                validateFirst
                rules={[{ required: false }, { type: "number" }]}
              />
            </Form.Item>

            <Form.Item validateStatus={exceedsHerdSize ? "error" : ""}>
              <InputNumber
                disabled={readOnly}
                name="cowSalesForStandardManufacturing"
                dataTestId="footprinting-herd-size-calves-input"
                label={t("dairyHerdCommercialClassesForm.question.standardManufacturingInput")}
                suffix={t("dairyHerdCommercialClassesForm.question.commercialClassesSuffix")}
                value={cowSalesForStandardManufacturing}
                initialValue={cowSalesForStandardManufacturing}
                onChange={(v) => {
                  setCowSalesForStandardManufacturing(v);
                }}
                validateFirst
                rules={[{ required: false }, { type: "number" }]}
              />
            </Form.Item>

            <Form.Item validateStatus={exceedsHerdSize ? "error" : ""}>
              <InputNumber
                disabled={readOnly}
                name="cowSalesInGoodCondition"
                dataTestId="footprinting-herd-size-calves-input"
                label={t("dairyHerdCommercialClassesForm.question.goodConditionInput")}
                suffix={t("dairyHerdCommercialClassesForm.question.commercialClassesSuffix")}
                value={cowSalesInGoodCondition}
                initialValue={cowSalesInGoodCondition}
                onChange={(v) => {
                  setCowSalesInGoodCondition(v);
                }}
                validateFirst
                rules={[{ required: false }, { type: "number" }]}
              />
            </Form.Item>

            <Form.Item validateStatus={exceedsHerdSize ? "error" : ""}>
              <InputNumber
                disabled={readOnly}
                name="cowSalesInPremiumCondition"
                dataTestId="footprinting-herd-size-calves-input"
                label={t("dairyHerdCommercialClassesForm.question.premiumConditionInput")}
                suffix={t("dairyHerdCommercialClassesForm.question.commercialClassesSuffix")}
                value={cowSalesInPremiumCondition}
                initialValue={cowSalesInPremiumCondition}
                onChange={(v) => {
                  setCowSalesInPremiumCondition(v);
                }}
                validateFirst
                rules={[{ required: false }, { type: "number" }]}
              />
            </Form.Item>
          </Flex>
        </Form>
        <Flex className="dairy-display-information-card flex-center padding-100 margin-top-150" key={totalCowSales}>
          <p className="paragraph-s grey-600-text margin-0 margin-bottom-25">{t("dairyHerdCommercialClassesForm.herdCommercialClassesDisplayTitle")}</p>
          <h5 className={`center ${exceedsHerdSize ? "red-text" : ""}`}>
            {`${totalCowSales}/${totalHerdSize} ${t("dairyHerdCommercialClassesForm.herdCommercialClassesDisplayUnit")}`}
          </h5>
        </Flex>

        {exceedsHerdSize && (
          <AlertBanner
            gap={4}
            className="herd-commercial-class-alert-banner margin-top-100"
            description={t("dairyHerdCommercialClassesForm.error.description")}
            linkText={t("dairyHerdCommercialClassesForm.error.link")}
            link={true}
            onClick={() => onNavigateToStep?.(1)}
            iconName="tabler/alert-triangle"
            iconSize={24}
            iconAlign="start"
            type="warning"
          />
        )}
      </Card>
    </div>
  );
};
